/**
 * Alias-Host-Registry — panel-eigene Wahrheit ueber provisionierte
 * Merchant-Journey-Hosts (Spec 516, Contract v2.20).
 *
 * `publicBaseUrl` am Facade-Service MUSS einer der von der Facade
 * infrastrukturseitig provisionierten Alias-Hosts sein (CloudFront + Cert +
 * DNS). Die Facade validiert das NICHT — der Picker (kein Freitext) auf Basis
 * dieser Registry ist die Konsistenz-Grenze. Ein Wert ausserhalb der Liste
 * erzeugt eine tote Journey ohne Fehlermeldung.
 *
 * Pflege: Code-Change auf Facade-Host-Meldung (Handover-Ablauf Schritt 3,
 * `2026-08-01_info_merchant-domain-steuerung-rollen-und-ablauf.md`). Der bei
 * der Facade bestellte Read-Endpoint fuer die Host-Liste je Merchant loest
 * diese statische Registry spaeter ab (Spec 516 Non-Goal 1).
 *
 * Merchant-IDs live verifiziert 2026-08-01 gegen die Facade-Merchant-Ressource
 * (`GET /api/v1/admin/merchants/{id}` je Stage):
 *   dev     -> `gameloft`      (200, "Gameloft DEV"; `gameloft-prod` = 404)
 *   testing -> `gameloft-prod` (200; `gameloft` = 404)
 *   staging -> `gameloft-prod` (200; `gameloft` = 404; externer Alias
 *              `gameloft-staging.kanzaroo.com` seit 2026-08-02 provisioniert,
 *              Facade-Handover 2026-08-03)
 *   prod    -> `gameloft-prod` (Facade-Handover 2026-08-03: Unified-DNS auf
 *              prod abgeschlossen, beide Alias-Hosts End-to-End abgenommen,
 *              `GET /admin/edge/alias-hosts` liefert beide mit Stage "prod";
 *              die Zwei-Host-Liste gehoert komplett zu Gameloft).
 */

import type { PayStage, PayStageEntry } from './stage-map';

export interface AliasHostEntry {
  stage: PayStage;
  /** Facade-Merchant-ID, der dieser Alias-Host provisioniert ist. */
  merchantId: string;
  /** Kanonischer Origin (https, lowercase, ohne Trailing-Slash). */
  host: string;
}

export const ALIAS_HOST_REGISTRY: ReadonlyArray<AliasHostEntry> = [
  {
    stage: 'dev',
    merchantId: 'gameloft',
    host: 'https://gameloft.dev.facade.dcb.vasp.kanzaroo.com',
  },
  {
    stage: 'testing',
    merchantId: 'gameloft-prod',
    host: 'https://gameloft.testing.facade.dcb.vasp.kanzaroo.com',
  },
  {
    stage: 'staging',
    merchantId: 'gameloft-prod',
    host: 'https://gameloft.staging.facade.dcb.vasp.kanzaroo.com',
  },
  {
    stage: 'staging',
    merchantId: 'gameloft-prod',
    host: 'https://gameloft-staging.kanzaroo.com',
  },
  // Prod-Aliase OHNE Stage-Segment (interne Prod-Besonderheit) bzw. auf der
  // externen Merchant-Domain — nicht per Template aus den non-prod-Hosts
  // ableiten (dieselbe Falle wie DEFAULT_JOURNEY_HOSTS / stage-map.ts).
  {
    stage: 'prod',
    merchantId: 'gameloft-prod',
    host: 'https://gameloft.facade.dcb.vasp.kanzaroo.com',
  },
  {
    stage: 'prod',
    merchantId: 'gameloft-prod',
    host: 'https://gameloft.kanzaroo.com',
  },
];

/**
 * Default-Journey-Host je Stage (`api.<stage>` bzw. prod OHNE Stage-Segment) —
 * das ist der Host, auf den die Facade ohne gesetztes `publicBaseUrl`
 * zurueckfaellt. Literale Map, NICHT per String-Template ableiten (Spec 516
 * Trap 4 — dieselbe Prod-Falle wie bei `pay.<stage>` in `stage-map.ts`).
 * Bewusst eigene Map statt Import des devportal-Snippets (track-owned, Trap 7).
 */
export const DEFAULT_JOURNEY_HOSTS: Readonly<Record<PayStage, string>> = {
  dev: 'https://api.dev.facade.dcb.vasp.kanzaroo.com',
  testing: 'https://api.testing.facade.dcb.vasp.kanzaroo.com',
  staging: 'https://api.staging.facade.dcb.vasp.kanzaroo.com',
  prod: 'https://api.facade.dcb.vasp.kanzaroo.com',
};

/** Provisionierte Alias-Hosts fuer einen Merchant auf einer Stage. */
export function aliasHostsFor(stage: PayStage, merchantId: string): string[] {
  return ALIAS_HOST_REGISTRY.filter(
    (entry) => entry.stage === stage && entry.merchantId === merchantId
  ).map((entry) => entry.host);
}

/** Default-Journey-Host (`api.…`) einer Stage. */
export function defaultJourneyHost(stage: PayStage): string {
  return DEFAULT_JOURNEY_HOSTS[stage];
}

/**
 * Origin-Normalisierung fuer Vergleiche (Spec 516 Trap 6): lowercase Host,
 * Trailing-Slash weg. `null`, wenn der Wert keine parsebare absolute URL ist —
 * Aufrufer behandeln das als "kein Origin" (z. B. Registry-fremd-Markierung).
 */
export function normalizeOrigin(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * EIN Aufloesungspunkt fuer die Link-Base (Spec 516 D-7): `publicBaseUrl` des
 * Service gewinnt (normalisierter Origin, `placeholder: false` — ein
 * provisionierter Alias unterliegt nie einer Basis-Sperre des Stage-Entry),
 * sonst das Stage-Entry unveraendert. Rueckgabe ist ein
 * ECHTES `PayStageEntry` + `source`, damit alle Konsumstellen
 * (`buildPreviewSegments`, `buildFullUrl`, Snippet-Kontext) ihre Signaturen
 * behalten. Unparsebare Werte fallen defensiv auf den Stage-Default zurueck
 * (der BFF validiert schreibend, Alt-/Direktwrite-Werte sollen nie werfen).
 */
export type ResolvedLinkBase = PayStageEntry & {
  source: 'service-alias' | 'stage-default';
};

export function resolveLinkBase(
  publicBaseUrl: string | undefined,
  stageEntry: PayStageEntry,
): ResolvedLinkBase {
  if (publicBaseUrl) {
    const origin = normalizeOrigin(publicBaseUrl);
    if (origin) {
      return {
        stage: stageEntry.stage,
        payBaseUrl: origin,
        placeholder: false,
        source: 'service-alias',
      };
    }
  }
  return { ...stageEntry, source: 'stage-default' };
}

/**
 * Registry-Eintrag zu einem Origin (fuer die Analyse-Modus-Erkennung,
 * Spec 516 Scope 5): Alias-Host -> seine Stage + Merchant. `null` = kein
 * bekannter Alias-Host. Vergleich origin-normalisiert auf beiden Seiten.
 */
export function aliasEntryForOrigin(
  origin: string
): { stage: PayStage; merchantId: string } | null {
  const normalized = normalizeOrigin(origin);
  if (!normalized) {
    return null;
  }
  const entry = ALIAS_HOST_REGISTRY.find((e) => e.host === normalized);
  return entry ? { stage: entry.stage, merchantId: entry.merchantId } : null;
}
